import React from 'react';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { useRouter } from 'next/router';
import { Typography } from '@kiiwi/ui';
import Link from 'next/link';
import { getCardFullName, getSubstitute } from '@/components/utils';
import PractitionerFlag from './PractitionerFlag';
import CenterFlag from './CenterFlag';
import { useTranslation } from '@/src/i18n';
import useResponsive from '@/src/hooks/useResponsive';

interface Props {
  item: Required<CardHcd>;
  onClick?: (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => void;
}

const SearchCardName: React.FC<Props> = ({ item }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;
  const { pathname } = useRouter();
  const { isSubstitute: hasSubstituteData, substitute } = getSubstitute(item);
  const isSubstitute =
    hasSubstituteData && pathname.includes('teleconsultation');
  const fullNamePractitioner = isSubstitute
    ? `${substitute?.firstName} ${substitute?.lastName}`
    : getCardFullName(t, item);

  const content = React.useMemo(() => {
    return (
      <a
        className="flex gap-2 hover:underline"
        data-cy="practitioner-name"
        title={fullNamePractitioner}
        aria-label={fullNamePractitioner}
        href="#"
        onClick={e => {
          (window as any).dataLayer?.push({
            event: 'SearchCardBody_clickOnCardTitle',
            center: item?.center,
            professional: item?.professional,
            type: item?.type,
          });
          // Set property to event for tell parent dont track again
          // We dont use stopPropagation because we need the parent handle this event too
          (e as any).tracked = true;
        }}
      >
        <Typography
          variant="body-1"
          className="truncate font-semibold leading-6 text-neutral-primary sm:text-lg lg:leading-7"
        >
          {fullNamePractitioner}
        </Typography>
        {item?.professional?.practitionerId ? (
          <PractitionerFlag
            practitionerId={item?.professional?.practitionerId}
          />
        ) : (
          <CenterFlag centerId={item?.center?.id} />
        )}
      </a>
    );
  }, [fullNamePractitioner, item]);

  return isMobileOrTablet ? (
    content
  ) : (
    <Link
      href={{
        pathname: item.url,
        query: {
          ...(item?.center?.id ? { centerId: item.center.id } : {}),
          ...(pathname.includes('teleconsultation') && {
            fromImmediateTlc: true,
          }),
        },
      }}
      passHref
      legacyBehavior
    >
      {content}
    </Link>
  );
};

export default SearchCardName;
